import { Component, OnInit } from '@angular/core';
import { CommonModule, DecimalPipe } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { AuthenticationService, VendorPlanFeatureService } from '@app/_services';
import { SharedService } from '@app/_services/shared.service';
import { SubscriptionProfileService } from './subscription-profile.service';
import { ToastrService } from 'ngx-toastr';
import { CurrencyConvertPipe } from '@app/shared/pipes';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { of } from 'rxjs';
import { catchError, timeout } from 'rxjs/operators';

@UntilDestroy()
@Component({
    selector: 'app-page-subscription-profile',
    templateUrl: './page-subscription-profile.component.html',
    styleUrls: ['./page-subscription-profile.component.scss'],
    standalone: true,
    imports: [CommonModule, TranslateModule, DecimalPipe, CurrencyConvertPipe],
    providers: [SubscriptionProfileService]
})
export class PageSubscriptionProfileComponent implements OnInit {
    subscription: any = null;
    subscriptionPlan: any = null; // Store the plan details for the current subscription
    isLoading: boolean = true;
    isCancelling: boolean = false;
    showCancelConfirm: boolean = false;
    
    // Trial cancellation
    isCancellingTrial: boolean = false;
    showCancelTrialConfirm: boolean = false;

    // Tab selection
    activeTab: 'plans' | 'history' = 'plans';

    // Purchase history
    purchaseHistory: any[] = [];
    isLoadingHistory: boolean = false;

    // Plan selection properties
    isCustomer: boolean = false;
    isVendor: boolean = false;
    isSupplier: boolean = false;
    customerPlans: any[] = [];
    isLoadingPlans: boolean = false;
    selectedPlan: any = null;
    selectedBillingType: { [planId: string]: 'monthly' | 'yearly' } = {};
    expandedFeatures: { [planId: string]: boolean } = {};
    isSubscribing: boolean = false;
    isTrialEligible: boolean = false; // Track if user is eligible for free trial
    userCountry: any = null;
    userRoles: string[] = [];

    // Role IDs from DB (same as vendor-form)
    private readonly VENDOR_ROLE_ID = '6040d231000ac86f58793001';
    private readonly SUPPLIER_ROLE_ID = '655f32ce2f02fe110f8907d3';

    // Feature map for populating features on plans
    private featureMap = new Map<string, any>();

    // Sub-feature cache (same approach as vendor-form)
    private subFeatureMap = new Map<string, any>();
    private subFeaturesByFeatureId = new Map<string, any[]>();
    private subFeaturesLoaded: { [featureId: string]: boolean } = {};

    // Auto-refresh
    private refreshInterval: any;
    isRefreshing: boolean = false;

    // Inventory counts for inventory-type features
    actualInventoryCounts: { [featureCode: string]: number } = {};
    isLoadingInventory: boolean = false;

    constructor(
        private authService: AuthenticationService,
        private subscriptionService: SubscriptionProfileService,
        private sharedService: SharedService,
        private toastr: ToastrService,
        private translate: TranslateService,
        private router: Router,
        private vendorPlanFeatureService: VendorPlanFeatureService
    ) {}

    ngOnInit(): void {
        this.checkUserType();
        this.loadSubscription();
        this.loadPurchaseHistory();
        this.startAutoRefresh();
    }

    ngOnDestroy(): void {
        this.stopAutoRefresh();
    }

    startAutoRefresh(): void {
        // Refresh subscription data every 30 seconds to keep feature usage updated
        this.refreshInterval = setInterval(() => {
            this.refreshSubscriptionData();
        }, 30000); // 30 seconds
    }

    stopAutoRefresh(): void {
        if (this.refreshInterval) {
            clearInterval(this.refreshInterval);
            this.refreshInterval = null;
        }
    }

    refreshSubscriptionData(): void {
        if (this.isRefreshing || !this.subscription) return;
        
        this.isRefreshing = true;
        this.subscriptionService.getMySubscription().pipe(untilDestroyed(this)).subscribe({
            next: (response: any) => {
                this.isRefreshing = false;
                if (response.code === 200 && response.data) {
                    // Refresh inventory counts after updating subscription
                    this.loadActualInventoryCounts();
                    this.subscription = response.data;
                    this.normalizeSubscriptionStatus();
                    // Load the plan details if subscription has a plan_id
                    if (this.subscription?.plan_id) {
                        this.loadSubscriptionPlanDetails(this.subscription.plan_id);
                    }
                }
            },
            error: (error) => {
                this.isRefreshing = false;
                console.error('Error refreshing subscription:', error);
            }
        });
    }

    manualRefresh(): void {
        this.refreshSubscriptionData();
        this.toastr.info(this.translate.instant('refreshing_data'));
    }

    checkUserType(): void {
        const user = this.authService.currentUserValue;
        
 
        
        // User object has isVendor boolean property, not type field
        // Vendor: user.isVendor === true
        // Supplier: user.isSupplier === true OR user.supplier === true
        // Customer: default (neither vendor nor supplier)
        // PRIORITY: Supplier > Vendor > Customer
        
        // Check both isSupplier and supplier properties (lowercase)
        this.isSupplier = user?.isSupplier === true || user?.supplier === true;
        this.isVendor = user?.isVendor === true;
        
        // Apply priority: If supplier, then NOT vendor (mutually exclusive)
        if (this.isSupplier) {
            this.isVendor = false;
            this.isCustomer = false;
        } else if (this.isVendor) {
            this.isSupplier = false;
            this.isCustomer = false;
        } else {
            this.isCustomer = true;
        }
        
        // Check company type if roles are not available
        if (user?.company?.type) {
            const companyType = user.company.type.toLowerCase();
            
            
            // Priority: Supplier > Vendor > Customer
            if (companyType === 'supplier' || companyType === 'sp') {
                this.isSupplier = true;
                this.isVendor = false;
                this.isCustomer = false;
            } else if (companyType === 'vendor' || companyType === 'v') {
                // Only set vendor if not already supplier
                if (!this.isSupplier) {
                    this.isVendor = true;
                    this.isSupplier = false;
                    this.isCustomer = false;
                }
            }
        }
        
        // Also check roles array if available - PRIORITY: Supplier > Vendor > Customer
        const roles: string[] = Array.isArray(user?.roles) ? user.roles : [];
        this.userRoles = roles;
        
        // If roles exist, check by role IDs with priority (roles override everything else)
        if (roles.length > 0) {
            // Supplier takes priority over Vendor
            if (roles.includes(this.SUPPLIER_ROLE_ID)) {
                this.isSupplier = true;
                this.isVendor = false;
                this.isCustomer = false;
            } else if (roles.includes(this.VENDOR_ROLE_ID)) {
                // Only set vendor if not already supplier
                if (!this.isSupplier) {
                    this.isVendor = true;
                    this.isSupplier = false;
                    this.isCustomer = false;
                }
            }
        }
        
        
        
        // Get country from SharedService (selected country) or fall back to user's profile country
        const selectedCountry = this.sharedService.selectedCountry;
        if (selectedCountry) {
            // Use the selected country's name (e.g., { en: 'Bahrain', ar: 'البحرین' })
            this.userCountry = selectedCountry.name || selectedCountry;
        } else {
            this.userCountry = user?.country || null;
        }
        
      
        
        // Always load plans - user type determines filtering
        this.loadPlans();
    }

    loadPlans(): void {
        this.isLoadingPlans = true;
        
        // Check trial eligibility first
        this.subscriptionService.checkTrialEligibility().pipe(untilDestroyed(this)).subscribe({
            next: (response: any) => {
                this.isTrialEligible = response?.code === 200 && response?.data?.eligible === true;
            },
            error: (error) => {
                this.isTrialEligible = false;
            }
        });
        
        // First, load active features and build a map for id -> feature (same as vendor-form)
        this.vendorPlanFeatureService.getFeatures({ page: 1, limit: 1000, status: 'A' }).subscribe({
            next: (featuresRes: any) => {
                if (featuresRes?.code === 200) {
                    const features = featuresRes.data || [];
                    this.featureMap = new Map(features.map((f: any) => [f._id, f]));
                } else {
                    this.featureMap = new Map();
                }

                // Then load plans and enrich with populated features if missing
                this.subscriptionService.getAllPlans().pipe(untilDestroyed(this)).subscribe({
                    next: (response: any) => {
                        this.isLoadingPlans = false;
                        if (response.code === 200 && response.data) {
                            // Populate features on each plan
                            const plansWithFeatures = (response.data || []).map((plan: any) => {
                                // If already populated by backend, use it
                                if (Array.isArray(plan.subscription_features_populated) && plan.subscription_features_populated.length > 0) {
                                    return plan;
                                }
                                // Otherwise, populate from subscription_features array using the featureMap
                                const ids: string[] = Array.isArray(plan.subscription_features) ? plan.subscription_features : [];
                                const populated = ids.map((id: string) => this.featureMap.get(id)).filter(Boolean);
                                return { ...plan, subscription_features_populated: populated };
                            });
                            this.filterPlans(plansWithFeatures);
                        }
                    },
                    error: (error) => {
                        this.isLoadingPlans = false;
                        console.error('Error loading plans:', error);
                    }
                });
            },
            error: () => {
                // Fallback: still load plans even if features call fails
                this.subscriptionService.getAllPlans().pipe(untilDestroyed(this)).subscribe({
                    next: (response: any) => {
                        this.isLoadingPlans = false;
                        if (response.code === 200 && response.data) {
                            this.filterPlans(response.data);
                        }
                    },
                    error: (error) => {
                        this.isLoadingPlans = false;
                        console.error('Error loading plans:', error);
                    }
                });
            }
        });
    }

    filterPlans(allPlans: any[]): void {
        if (!allPlans) {
            this.customerPlans = [];
            return;
        }


        const userCountryEn = (this.userCountry?.en || this.userCountry || '').toString().toLowerCase().trim();
        const userCountryAr = (this.userCountry?.ar || '').toString().toLowerCase().trim();

     

        // Determine required userType and roleId based on user type
        // Priority: Vendor > Supplier > Customer (vendor/supplier should never fall through to customer)
        let requiredUserType: string;
        let requiredRoleId: string | null = null;

        if (this.isVendor) {
            requiredUserType = 'VA';
            requiredRoleId = this.VENDOR_ROLE_ID;
        } else if (this.isSupplier) {
            requiredUserType = 'VA'; // Suppliers also use VA userType but with SP role
            requiredRoleId = this.SUPPLIER_ROLE_ID;
        } else if (this.isCustomer) {
            requiredUserType = 'C';
        } else {
            this.customerPlans = [];
            return;
        }

      

        this.customerPlans = allPlans.filter(plan => {
            

            // Exclude global default plans - they are auto-assigned
            if (plan.isGlobalDefault === true) {

                return false;
            }

            // Exclude custom assign plans - they are manually assigned
            if (plan.isCustomAssign === true) {
                return false;
            }

            // Exclude default plans (free default plans already assigned)
            if (plan.isDefault === true) {
             
                return false;
            }

            // Must be active
            if (plan.status !== 'A') {
              
                return false;
            }

            // Check userType based on user's type - use consistent logic with priority order
            if (this.isVendor || this.isSupplier) {
                // Vendor/Supplier plans have userType 'VA', 'VendorAdmin', or 'supplier'
                if (plan.userType !== 'VA' && plan.userType !== 'VendorAdmin' && plan.userType !== 'supplier') {
                 
                    return false;
                }

                // For vendor/supplier, also check roles
                if (requiredRoleId) {
                    const planRoles: string[] = Array.isArray(plan.roles) ? plan.roles : [];
                  
                    
                    if (!planRoles.includes(requiredRoleId)) {
                  
                        return false;
                    }
                }
            } else {
                // Customer plans have userType 'C' or 'Customer'
                if (plan.userType !== 'C' && plan.userType !== 'Customer') {
                    
                    return false;
                }
            }

            // Must have a price (0 is valid for free plans)
            const hasPrice = (plan.monthly_price !== undefined && plan.monthly_price !== null && plan.monthly_price !== '') ||
                             (plan.yearly_price !== undefined && plan.yearly_price !== null && plan.yearly_price !== '');
            if (!hasPrice) {
                
                return false;
            }

            // Filter by country
            if (plan.country) {
                const planCountryEn = (plan.country.en || plan.country || '').toString().toLowerCase().trim();
                const planCountryAr = (plan.country.ar || '').toString().toLowerCase().trim();

              

                const isAllCountries = planCountryEn === 'all countries' || planCountryAr === 'جميع الدول';
                const countryMatch = planCountryEn === userCountryEn || planCountryAr === userCountryAr;

                // Also check common abbreviations
                const abbreviationMatch = this.matchCountryAbbreviation(planCountryEn, userCountryEn);

               
                if (!isAllCountries && !countryMatch && !abbreviationMatch && userCountryEn) {
                   
                    return false;
                }
            }

          
            return true;
        });

        

        // Sort plans: free plans first, then by price (ascending)
        this.customerPlans.sort((a, b) => {
            // Free plans come first
            const aIsFree = a.isFree || (a.monthly_price === 0 && a.yearly_price === 0);
            const bIsFree = b.isFree || (b.monthly_price === 0 && b.yearly_price === 0);
            
            if (aIsFree && !bIsFree) return -1;
            if (!aIsFree && bIsFree) return 1;

            // Then by price ascending
            const aPrice = a.monthly_price || a.yearly_price || 0;
            const bPrice = b.monthly_price || b.yearly_price || 0;
            return aPrice - bPrice;
        });

        // Initialize billing types
        this.customerPlans.forEach(plan => {
            this.selectedBillingType[plan._id] = plan.monthly_price ? 'monthly' : 'yearly';
        });
    }

    // Helper method to match country abbreviations (same as vendor-form)
    private matchCountryAbbreviation(planCountry: string, selectedCountry: string): boolean {
        const abbreviationMap: { [key: string]: string[] } = {
            'bahrain': ['bhr', 'bh', 'bahrain'],
            'saudi arabia': ['ksa', 'sa', 'saudi', 'saudi arabia'],
            'united arab emirates': ['uae', 'ae', 'emirates', 'united arab emirates'],
            'kuwait': ['kwt', 'kw', 'kuwait'],
            'oman': ['omn', 'om', 'oman'],
            'qatar': ['qat', 'qa', 'qatar']
        };

        for (const [country, abbreviations] of Object.entries(abbreviationMap)) {
            const planMatches = abbreviations.some(abbr => planCountry.includes(abbr) || abbr.includes(planCountry));
            const selectedMatches = abbreviations.some(abbr => selectedCountry.includes(abbr) || abbr.includes(selectedCountry));
            
            if (planMatches && selectedMatches) {
                return true;
            }
        }

        return false;
    }

    // Keep old method name for backwards compatibility with template
    loadCustomerPlans(): void {
        this.loadPlans();
    }

    filterCustomerPlans(allPlans: any[]): void {
        this.filterPlans(allPlans);
    }

    loadSubscription(): void {
        this.isLoading = true;
        this.subscriptionService.getMySubscription().pipe(untilDestroyed(this)).subscribe({
            next: (response: any) => {
                this.isLoading = false;
                if (response.code === 200 && response.data) {
                    this.subscription = response.data;
                    this.normalizeSubscriptionStatus();
                    // Load the plan details if subscription has a plan_id
                    if (this.subscription?.plan_id) {
                        this.loadSubscriptionPlanDetails(this.subscription.plan_id);
                    }
                } else {
                    this.subscription = null;
                    this.subscriptionPlan = null;
                }
            },
            error: (error) => {
                this.isLoading = false;
                console.error('Error loading subscription:', error);
                this.subscription = null;
                this.subscriptionPlan = null;
            }
        });
    }

    // Load subscription plan details
    loadSubscriptionPlanDetails(planId: string): void {
        this.subscriptionService.getPlanById(planId).pipe(untilDestroyed(this)).subscribe({
            next: (response: any) => {
                if (response.code === 200 && response.data) {
                    this.subscriptionPlan = response.data;
                    // Populate features if needed
                    if (this.subscriptionPlan && (!this.subscriptionPlan.subscription_features_populated || this.subscriptionPlan.subscription_features_populated.length === 0)) {
                        const ids: string[] = Array.isArray(this.subscriptionPlan.subscription_features) ? this.subscriptionPlan.subscription_features : [];
                        const populated = ids.map((id: string) => this.featureMap.get(id)).filter(Boolean);
                        this.subscriptionPlan.subscription_features_populated = populated;
                    }
                    // Load sub-features for each feature
                    const featureIds = this.subscriptionPlan?.subscription_features_populated?.map((f: any) => this.toId(f._id)) || [];
                    featureIds.forEach((fid: string) => this.ensureSubFeaturesLoaded(fid));
                    
                    // Load actual inventory counts for inventory features
                    this.loadActualInventoryCounts();
                } else {
                    this.subscriptionPlan = null;
                }
            },
            error: (error) => {
                console.error('Error loading subscription plan:', error);
                this.subscriptionPlan = null;
            }
        });
    }

    // Tab management
    setActiveTab(tab: 'plans' | 'history'): void {
        this.activeTab = tab;
    }

    // Load purchase history
    loadPurchaseHistory(): void {
        this.isLoadingHistory = true;
        this.subscriptionService.getPurchaseHistory().pipe(untilDestroyed(this)).subscribe({
            next: (response: any) => {
                this.isLoadingHistory = false;
                if (response.success && response.data) {
                    this.purchaseHistory = response.data;
                } else {
                    this.purchaseHistory = [];
                }
            },
            error: (error) => {
                this.isLoadingHistory = false;
                console.error('Error loading purchase history:', error);
                this.purchaseHistory = [];
            }
        });
    }

    // Format purchase date
    formatPurchaseDate(date: string | Date): string {
        if (!date) return '-';
        return new Date(date).toLocaleDateString(this.translate.currentLang === 'ar' ? 'ar-BH' : 'en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    }

    // Get localized plan name from purchase history
    getPurchasePlanName(purchase: any): string {
        if (!purchase?.planName) return '-';
        const lang = this.translate.currentLang || 'en';
        return purchase.planName[lang] || purchase.planName.en || purchase.planName.ar || '-';
    }

    // Format number for display
    formatNumber(num: number): string {
        return num?.toLocaleString() || '0';
    }

    getStatusClass(status: string): string {
        switch (status?.toLowerCase()) {
            case 'active':
                return 'status-active';
            case 'trial':
                return 'status-trial';
            case 'cancelled':
                return 'status-cancelled';
            case 'pending':
                return 'status-pending';
            case 'expired':
                return 'status-expired';
            default:
                return 'status-default';
        }
    }

    /**
     * Check if user can subscribe to a new plan
     * Only allowed if current plan's payment_method is 'default_plan' or 'free', or if subscription is expired
     */
    canSubscribeToNewPlan(): boolean {
        // If no subscription exists, user can subscribe
        if (!this.subscription) {
            return true;
        }
        
        // Allow if subscription is expired or cancelled
        const status = this.subscription.status?.toLowerCase();
        if (status === 'expired' || status === 'cancelled') {
            return true;
        }
        
        // Allow if payment method is default_plan, free, or custom_assign
        const paymentMethod = this.subscription.payment_method?.toLowerCase();
        return paymentMethod === 'default_plan' || paymentMethod === 'free' || paymentMethod === 'custom_assign';
    }

    getPaymentStatusClass(status: string): string {
        switch (status?.toLowerCase()) {
            case 'done':
            case 'paid':
                return 'status-active';
            case 'pending':
                return 'status-pending';
            case 'failed':
                return 'status-cancelled';
            default:
                return 'status-default';
        }
    }

    formatDate(date: string | Date): string {
        if (!date) return '-';
        return new Date(date).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    }

    getDaysRemaining(): number {
        // If subscription is cancelled, return 0
        if (this.subscription?.status === 'cancelled') return 0;
        
        if (!this.subscription?.end_date) return 0;
        const endDate = new Date(this.subscription.end_date);
        const today = new Date();
        
        // Normalize both dates to start of day (00:00:00) for consistent calculation
        endDate.setHours(0, 0, 0, 0);
        today.setHours(0, 0, 0, 0);
        
        const diffTime = endDate.getTime() - today.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return diffDays > 0 ? diffDays : 0;
    }

    getPlanName(): string {
        if (!this.subscription?.plan_name) return '-';
        const planName = this.subscription.plan_name;
        if (typeof planName === 'object') {
            return planName.en || planName.ar || '-';
        }
        return planName;
    }

    showCancelDialog(): void {
        this.showCancelConfirm = true;
    }

    hideCancelDialog(): void {
        this.showCancelConfirm = false;
    }

    isFreeOrDefaultPlan(): boolean {
        if (!this.subscription) return false;
        
        // Check if it's a free plan
        if (this.subscription.is_free_plan === true) return true;
        
        // Check if payment method is 'free', 'default_plan', or 'custom_assign'
        const paymentMethod = this.subscription.payment_method?.toLowerCase();
        return paymentMethod === 'free' || paymentMethod === 'default_plan' || paymentMethod === 'custom_assign';
    }

    confirmCancel(): void {
        if (!this.subscription?._id) return;

        this.isCancelling = true;
        
        // For paid plans, schedule cancellation at end of period
        this.subscriptionService.cancelSubscription(this.subscription._id, { cancel_at_period_end: true }).pipe(untilDestroyed(this)).subscribe({
            next: (response: any) => {
                this.isCancelling = false;
                this.showCancelConfirm = false;
                if (response.code === 200) {
                    // Update subscription to show it's scheduled for cancellation
                    if (this.subscription) {
                        this.subscription.cancel_at_period_end = true;
                        this.subscription.status = 'active';
                    }
                    // Show success notification
                    this.toastr.success(this.translate.instant('cancellation_scheduled_successfully'));
                } else {
                    this.toastr.error(response.msg || this.translate.instant('error_scheduling_cancellation'));
                }
            },
            error: (error) => {
                this.isCancelling = false;
                this.showCancelConfirm = false;
                console.error('Error scheduling cancellation:', error);
                this.toastr.error(this.translate.instant('error_scheduling_cancellation'));
            }
        });
    }

    /**
     * Normalize subscription status for scheduled cancellations
     */
    private normalizeSubscriptionStatus(): void {
        if (!this.subscription) return;
        
        const cancelScheduled = this.subscription.cancel_at_period_end === true || 
                               this.subscription.cancelAtPeriodEnd === true;
        
        if (cancelScheduled && this.subscription.end_date) {
            const endDate = new Date(this.subscription.end_date);
            const now = new Date();
            
            if (endDate > now) {
                this.subscription.status = 'active';
                this.subscription.cancel_at_period_end = true;
            } else {
                this.subscription.status = 'cancelled';
                this.subscription.cancel_at_period_end = false;
            }
        }
    }
    
    /**
     * Check if subscription is in trial period
     */
    isInTrialPeriod(): boolean {
        if (!this.subscription) return false;
        
        const status = this.subscription.status?.toLowerCase();
        const paymentStatus = this.subscription.payment_status?.toLowerCase();
        const trialEndDate = this.subscription.processerValues?.trialEndDate;
        
        // Trial period conditions:
        // 1. Status is 'trial' (new) or 'pending' (legacy) — not yet activated
        // 2. Payment status is 'authorized' (not yet captured)
        // 3. Trial end date exists and is in the future
        if ((status === 'trial' || status === 'pending') && paymentStatus === 'authorized' && trialEndDate) {
            const trialEnd = new Date(trialEndDate);
            const now = new Date();
            return trialEnd > now;
        }
        
        return false;
    }
    
    /**
     * Get days remaining in trial period
     */
    getTrialDaysRemaining(): number {
        if (!this.subscription?.processerValues?.trialEndDate) return 0;
        
        const trialEnd = new Date(this.subscription.processerValues.trialEndDate);
        const now = new Date();
        
        // Normalize both dates to start of day (00:00:00) for consistent calculation
        trialEnd.setHours(0, 0, 0, 0);
        now.setHours(0, 0, 0, 0);
        
        const diffTime = trialEnd.getTime() - now.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        
        return diffDays > 0 ? diffDays : 0;
    }
    
    /**
     * Show trial cancellation dialog
     */
    showCancelTrialDialog(): void {
        this.showCancelTrialConfirm = true;
    }
    
    /**
     * Hide trial cancellation dialog
     */
    hideCancelTrialDialog(): void {
        this.showCancelTrialConfirm = false;
    }
    
    /**
     * Confirm trial cancellation - voids the authorization
     */
    confirmCancelTrial(): void {
        if (!this.subscription?._id) return;
        
        this.isCancellingTrial = true;
        this.showCancelTrialConfirm = false;
        
        // Call the void endpoint to cancel trial authorization
        this.subscriptionService.cancelTrial(this.subscription._id).pipe(
            timeout(30000),
            catchError(err => {
                throw err;
            }),
            untilDestroyed(this)
        ).subscribe({
            next: (response: any) => {
                this.isCancellingTrial = false;
                
                // Check the actual response structure from backend: { code: 200, data: { success: true, message: '...' } }
                if (response?.code === 200 && response?.data?.success) {
                    this.toastr.success(response.data.message || this.translate.instant('trial_cancelled_successfully'));
                    this.loadSubscription();
                } else {
                    this.toastr.error(response?.data?.message || this.translate.instant('trial_cancellation_failed'));
                }
            },
            error: (error) => {
                this.isCancellingTrial = false;
                
                // Check if it's actually a successful response misinterpreted as error
                if (error?.status === 200 && error?.error?.code === 200) {
                    this.toastr.success(this.translate.instant('trial_cancelled_successfully'));
                    this.loadSubscription();
                } else {
                    const errorMessage = error?.error?.message || error?.message || this.translate.instant('trial_cancellation_error');
                    this.toastr.error(errorMessage);
                }
            }
        });
    }

    getUsagePercentage(): number {
        if (!this.subscription?.ai_tokens_total || this.subscription.ai_tokens_total === 0) return 0;
        return (this.subscription.ai_tokens_remaining / this.subscription.ai_tokens_total) * 100;
    }

    getTokenUsagePercentage(): number {
        if (!this.subscription?.ai_tokens_total || this.subscription.ai_tokens_total === 0) return 0;
        const used = this.subscription.ai_tokens_total - (this.subscription.ai_tokens_remaining || 0);
        return (used / this.subscription.ai_tokens_total) * 100;
    }

    getFeatureUsagePercentage(feature: any): number {
        if (!feature || feature.limit === null || feature.limit === -1 || feature.limit === 0) return 0;
        const used = feature.used || 0;
        return Math.min((used / feature.limit) * 100, 100);
    }

    getSubFeatureUsagePercentage(subFeature: any): number {
        if (!subFeature || subFeature.limit === null || subFeature.limit === -1 || subFeature.limit === 0) return 0;
        const used = subFeature.used || 0;
        return Math.min((used / subFeature.limit) * 100, 100);
    }

    // Load actual inventory counts for inventory-type features
    loadActualInventoryCounts(): void {
        if (!this.subscriptionPlan?.subscription_features_populated) {
            this.isLoadingInventory = false;
            return;
        }

        this.isLoadingInventory = true;
        const featureCodes = this.subscriptionPlan.subscription_features_populated
            .map((f: any) => f.featureCode)
            .filter((code: string) => code);

        if (featureCodes.length === 0) {
            this.isLoadingInventory = false;
            return;
        }

        // Call backend to get actual inventory counts
        this.subscriptionService.getMySubscriptionInventoryCounts(featureCodes).pipe(
            untilDestroyed(this),
            catchError(error => {
                console.error('Error loading inventory counts:', error);
                return of({ counts: {} });
            })
        ).subscribe(response => {
            this.actualInventoryCounts = response.counts || {};
            this.isLoadingInventory = false;
        });
    }

    // Get all features for the subscription with their usage
    getAllSubscriptionFeatures(): any[] {
        if (!this.subscriptionPlan?.subscription_features_populated) return [];
        
        return this.subscriptionPlan.subscription_features_populated.map((feature: any) => {
            const featureId = this.toId(feature._id);
            const featureCode = feature.featureCode;
            
            // Look up usage by featureCode (not by ID)
            const tracking = this.subscription?.feature_usage_tracking?.[featureCode] || { used: 0, limit: null };
            const limit = this.getFeatureUsageLimit(this.subscriptionPlan, featureId);
            
            // Check if we have actual inventory count for this feature
            const actualCount = this.actualInventoryCounts[featureCode];
            const isInventoryFeature = actualCount !== undefined;
            
            return {
                _id: featureId,
                featureCode: featureCode,
                name: feature.name,
                used: isInventoryFeature ? actualCount : (tracking.used || 0),
                limit: limit,
                isInventory: isInventoryFeature
            };
        });
    }

    // Get all sub-features for the subscription with their usage
    getAllSubscriptionSubFeatures(): any[] {
        if (!this.subscriptionPlan?.subscription_sub_features) return [];
        
        const subFeatures: any[] = [];
        const subFeatureIds = Array.isArray(this.subscriptionPlan.subscription_sub_features) 
            ? this.subscriptionPlan.subscription_sub_features.map((id: any) => this.toId(id))
            : [];
        
        subFeatureIds.forEach((subFeatureId: string) => {
            const subFeature = this.subFeatureMap.get(subFeatureId);
            if (subFeature) {
                const subFeatureCode = subFeature.subFeatureCode;
                
                // Look up usage by subFeatureCode (not by ID)
                const tracking = this.subscription?.sub_feature_usage_tracking?.[subFeatureCode] || { used: 0, limit: null };
                const limit = this.getSubFeatureUsageLimit(this.subscriptionPlan, subFeatureId);
                
                subFeatures.push({
                    _id: subFeatureId,
                    subFeatureCode: subFeatureCode,
                    name: subFeature.name,
                    parentFeature: subFeature.parentFeature,
                    used: tracking.used || 0,
                    limit: limit
                });
            }
        });
        
        return subFeatures;
    }

    // Get localized feature name
    getFeatureName(feature: any): string {
        const name = feature?.value?.name || feature?.name;
        if (!name) return feature?.key || '-';
        const lang = this.translate.currentLang || 'en';
        return name[lang] || name.en || name.ar || feature.key || '-';
    }

    // Get localized sub-feature name
    getSubFeatureName(subFeature: any): string {
        const name = subFeature?.value?.name || subFeature?.name;
        if (!name) return subFeature?.key || '-';
        const lang = this.translate.currentLang || 'en';
        return name[lang] || name.en || name.ar || subFeature.key || '-';
    }

    // Get localized parent feature name for a sub-feature
    getParentFeatureName(subFeature: any): string {
        const parentFeature = subFeature?.value?.parentFeature || subFeature?.parentFeature;
        if (!parentFeature?.name) return '';
        const lang = this.translate.currentLang || 'en';
        return parentFeature.name[lang] || parentFeature.name.en || parentFeature.name.ar || '';
    }

    // Get parent feature code for a sub-feature
    getParentFeatureCode(subFeature: any): string {
        return subFeature?.value?.parentFeature?.featureCode || '';
    }

    // Plan card helper methods
    localize(obj: any): string {
        if (!obj) return '';
        if (typeof obj === 'string') return obj;
        const lang = this.translate.currentLang || 'en';
        return obj[lang] || obj.en || obj.ar || '';
    }

    getCardGradient(index: number): string {
        const gradients = [
            'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
            'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)',
            'linear-gradient(135deg, #14b8a6 0%, #0d9488 100%)',
            'linear-gradient(135deg, #f97316 0%, #ea580c 100%)'
        ];
        return gradients[index % gradients.length];
    }

    getCardColor(index: number): any {
        const colors = [
            { bg: 'card-blue', text: 'text-white', priceText: 'text-white', oldPriceText: 'text-blue-200', iconText: 'text-blue-200', badge: 'bg-blue-800' },
            { bg: 'card-purple', text: 'text-white', priceText: 'text-white', oldPriceText: 'text-purple-200', iconText: 'text-purple-200', badge: 'bg-purple-800' },
            { bg: 'card-teal', text: 'text-white', priceText: 'text-white', oldPriceText: 'text-teal-200', iconText: 'text-teal-200', badge: 'bg-teal-800' },
            { bg: 'card-orange', text: 'text-white', priceText: 'text-white', oldPriceText: 'text-orange-200', iconText: 'text-orange-200', badge: 'bg-orange-800' }
        ];
        return colors[index % colors.length];
    }

    isPlanSelected(plan: any): boolean {
        return this.selectedPlan?._id === plan._id;
    }

    selectPlan(plan: any): void {
        this.selectedPlan = this.selectedPlan?._id === plan._id ? null : plan;

        if (this.selectedPlan) {
            const featureIds: string[] = Array.isArray(plan?.subscription_features)
                ? plan.subscription_features.map((id: any) => this.toId(id))
                : Array.isArray(plan?.subscription_features_populated)
                    ? plan.subscription_features_populated.map((f: any) => this.toId(f?._id)).filter(Boolean)
                    : [];
            featureIds.forEach((fid) => this.ensureSubFeaturesLoaded(fid));
        }
    }

    setBillingType(planId: string, type: 'monthly' | 'yearly'): void {
        this.selectedBillingType[planId] = type;
    }

    getDefaultBillingType(plan: any): 'monthly' | 'yearly' {
        return plan.monthly_price ? 'monthly' : 'yearly';
    }

    getCurrentBillingType(plan: any): 'monthly' | 'yearly' {
        return this.selectedBillingType[plan._id] || this.getDefaultBillingType(plan);
    }

    getDisplayPrice(plan: any): number {
        const billingType = this.getCurrentBillingType(plan);
        return billingType === 'monthly' ? (plan.monthly_price || 0) : (plan.yearly_price || 0);
    }

    toggleFeatures(planId: string): void {
        this.expandedFeatures[planId] = !this.expandedFeatures[planId];

        if (this.expandedFeatures[planId]) {
            const plan = (this.customerPlans || []).find((p: any) => p?._id === planId);
            const featureIds: string[] = Array.isArray(plan?.subscription_features)
                ? plan.subscription_features.map((id: any) => this.toId(id))
                : Array.isArray(plan?.subscription_features_populated)
                    ? plan.subscription_features_populated.map((f: any) => this.toId(f?._id)).filter(Boolean)
                    : [];
            featureIds.forEach((fid) => this.ensureSubFeaturesLoaded(fid));
        }
    }

    private toId(value: any): string {
        if (value === null || value === undefined) return '';
        return String(value);
    }

    private ensureSubFeaturesLoaded(featureId: string): void {
        const fid = this.toId(featureId);
        if (!fid) return;
        if (this.subFeaturesLoaded[fid] === true) return;

        this.subFeaturesLoaded[fid] = true;
        this.vendorPlanFeatureService.getSubFeatures(fid).pipe(untilDestroyed(this)).subscribe({
            next: (res: any) => {
                if (res?.code === 200) {
                    const list = Array.isArray(res.data) ? res.data : [];
                    this.subFeaturesByFeatureId.set(fid, list);
                    list.forEach((sf: any) => {
                        const sid = this.toId(sf?._id);
                        if (sid) this.subFeatureMap.set(sid, sf);
                    });
                } else {
                    this.subFeaturesByFeatureId.set(fid, []);
                }
            },
            error: () => {
                this.subFeaturesByFeatureId.set(fid, []);
            }
        });
    }

    getFeatureUsageLimit(plan: any, featureId: string): number | null {
        const fid = this.toId(featureId);
        if (!plan || !fid) return null;
        const limits = plan.feature_usage_limits || plan.featureUsageLimits;
        if (!limits || typeof limits !== 'object') return null;

        const raw = (limits as any)[fid] ?? (limits as any)[String(fid)];
        if (raw === null || raw === undefined || raw === '') return null;
        const num = typeof raw === 'number' ? raw : parseInt(String(raw), 10);
        return Number.isFinite(num) ? num : null;
    }

    getSubFeatureUsageLimit(plan: any, subFeatureId: string): number | null {
        const sid = this.toId(subFeatureId);
        if (!plan || !sid) return null;
        const limits = plan.sub_feature_usage_limits || plan.subFeatureUsageLimits;
        if (!limits || typeof limits !== 'object') return null;

        const raw = (limits as any)[sid] ?? (limits as any)[String(sid)];
        if (raw === null || raw === undefined || raw === '') return null;
        const num = typeof raw === 'number' ? raw : parseInt(String(raw), 10);
        return Number.isFinite(num) ? num : null;
    }

    getUsageLimitText(limit: number | null): string {
        if (limit === null || limit === undefined) return '';
        if (limit === -1) {
            const translated = this.translate.instant('unlimited');
            return translated && translated !== 'unlimited' ? translated : 'Unlimited';
        }
        return String(limit);
    }

    getPlanSubFeaturesForFeature(plan: any, featureId: string): any[] {
        const fid = this.toId(featureId);
        if (!plan || !fid) return [];
        const selectedIds = new Set<string>(
            (Array.isArray(plan?.subscription_sub_features) ? plan.subscription_sub_features : []).map((id: any) => this.toId(id))
        );
        if (selectedIds.size === 0) return [];

        const list = this.subFeaturesByFeatureId.get(fid) || [];
        return list.filter((sf: any) => selectedIds.has(this.toId(sf?._id)));
    }

    hasActiveDiscount(plan: any): boolean {
        const now = new Date();
        const billingType = this.getCurrentBillingType(plan);
        const discountRateField = billingType === 'monthly' ? 'monthly_discount_rate' : 'yearly_discount_rate';
        const discountStartsField = billingType === 'monthly' ? 'monthly_discount_starts' : 'yearly_discount_starts';
        const discountEndsField = billingType === 'monthly' ? 'monthly_discount_ends' : 'yearly_discount_ends';
        
        const discountRate = plan[discountRateField];
        if (!discountRate || discountRate <= 0) return false;
        
        const discountStart = plan[discountStartsField];
        const discountEnd = plan[discountEndsField];
        
        if (discountStart && new Date(discountStart) > now) return false;
        if (discountEnd && new Date(discountEnd) < now) return false;
        return true;
    }

    getCurrentDiscountRate(plan: any): number {
        if (!this.hasActiveDiscount(plan)) return 0;
        const billingType = this.getCurrentBillingType(plan);
        const discountRateField = billingType === 'monthly' ? 'monthly_discount_rate' : 'yearly_discount_rate';
        return plan[discountRateField] || 0;
    }

    calculateFinalPrice(price: number, plan: any): number {
        if (!this.hasActiveDiscount(plan)) return price;
        const discount = this.getCurrentDiscountRate(plan);
        return price * (1 - discount / 100);
    }

    subscribeToPlan(): void {
        if (!this.selectedPlan) {
            this.toastr.warning(this.translate.instant('please_select_plan'));
            return;
        }

        // Check if user can subscribe to a new plan
        if (!this.canSubscribeToNewPlan()) {
            this.toastr.warning(this.translate.instant('cannot_change_plan') || 'You cannot change your plan. Please contact support to upgrade or change your subscription.');
            return;
        }

        this.isSubscribing = true;
        const user = this.authService.currentUserValue;
        const billingType = this.getCurrentBillingType(this.selectedPlan);
        
        // Check if the plan is free (price is 0)
        const price = this.getDisplayPrice(this.selectedPlan);
        const finalPrice = this.calculateFinalPrice(price, this.selectedPlan);
        
        if (finalPrice === 0 || this.selectedPlan.isFree) {
            // For free plans, create subscription directly without checkout
            const subscriptionData = {
                user_id: user._id,
                plan_id: this.selectedPlan._id,
                billing_type: billingType,
                auto_renew: true,
                payment_method: 'free'
            };

            this.subscriptionService.createSubscription(subscriptionData).pipe(untilDestroyed(this)).subscribe({
                next: (response: any) => {
                    this.isSubscribing = false;
                    if (response.code === 200) {
                        this.toastr.success(this.translate.instant('subscription_created_successfully'));
                        this.selectedPlan = null;
                        this.loadSubscription();
                    } else {
                        this.toastr.error(response.msg || this.translate.instant('error_creating_subscription'));
                    }
                },
                error: (error) => {
                    this.isSubscribing = false;
                    console.error('Error creating subscription:', error);
                    this.toastr.error(this.translate.instant('error_creating_subscription'));
                }
            });
        } else {
            // For paid plans, redirect to checkout page
            this.proceedToCheckout(this.selectedPlan, billingType);
        }
    }

    // Proceed to checkout for paid plans
    private proceedToCheckout(plan: any, billingType: 'monthly' | 'yearly'): void {
        const priceField = billingType === 'monthly' ? 'monthly_price' : 'yearly_price';
        const selectedPrice = plan[priceField];
        
        // Calculate discount if applicable
        const discountRateField = billingType === 'monthly' ? 'monthly_discount_rate' : 'yearly_discount_rate';
        const discountStartsField = billingType === 'monthly' ? 'monthly_discount_starts' : 'yearly_discount_starts';
        const discountEndsField = billingType === 'monthly' ? 'monthly_discount_ends' : 'yearly_discount_ends';
        
        let discountRate = 0;
        let originalPrice = selectedPrice;
        let finalPrice = selectedPrice;
        
        // Check if discount is active
        if (plan[discountRateField] && plan[discountRateField] > 0) {
            const now = new Date();
            const discountStartDate = plan[discountStartsField] ? new Date(plan[discountStartsField]) : null;
            const discountEndDate = plan[discountEndsField] ? new Date(plan[discountEndsField]) : null;
            
            const isDiscountActive = (!discountStartDate || now >= discountStartDate) && 
                                    (!discountEndDate || now <= discountEndDate);
            
            if (isDiscountActive) {
                discountRate = plan[discountRateField];
                const discountAmount = originalPrice * (discountRate / 100);
                finalPrice = originalPrice - discountAmount;
            }
        }

        // Navigate to checkout-s route with subscription details
        // Using 'user-subscription' type for customer/user subscriptions (distinct from 'vendor-subscription')
        const subscriptionType = billingType === 'monthly' ? 'monthly' : 'annualy';
        const checkoutType = this.isVendor || this.isSupplier ? 'vendor-subscription' : 'user-subscription';
        
        this.isSubscribing = false;
        
        // Only pass subscription type - backend will fetch all other details securely
        this.router.navigate(['/checkout-s', plan._id, checkoutType], {
            queryParams: {
                st: subscriptionType
            }
        });
    }
}
